import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import Avatar from 'boring-avatars';
import {
    Diamond,
    History,
    UserPlus,
    Settings,
    LogOut,
    ChevronRight,
    Clock,
    CreditCard,
    RefreshCw,
    FileText,
    ExternalLink,
} from 'lucide-react';

const UserCenter = ({ isOpen, onClose, onLogout, onRecharge, onUpdateLog, onSettings }) => {
    const { token } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeSection, setActiveSection] = useState('main'); // 'main', 'history', 'invite', 'settings'

    const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || "";

    const fetchUserCenterData = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/accounts/user-center/`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) throw new Error('鑾峰彇鐢ㄦ埛鏁版嵁澶辫触');
            const result = await response.json();
            setData(result);
        } finally {
            setLoading(false);
        }
    }, [token, API_BASE_URL]);

    useEffect(() => {
        if (isOpen) {
            fetchUserCenterData();
            setActiveSection('main');
        }
    }, [isOpen, fetchUserCenterData]);

    if (!isOpen) return null;

    const userData = data?.user || {
        username: '加载中...',
        email: '',
        points: 0
    };

    const menuItems = [
        {
            icon: History,
            label: '账单记录',
            onClick: () => setActiveSection('history'),
            badge: data?.transactions?.count > 0 ? data.transactions.count : null
        },
        {
            icon: UserPlus,
            label: '邀请有礼',
            onClick: () => setActiveSection('invite'),
            badge: 'HOT'
        },
        {
            icon: Settings,
            label: '账户设置',
            onClick: () => { onClose?.(); onSettings?.(); },
            isExternal: true
        },
        // {
        //     icon: FileText,
        //     label: '更新日志',
        //     onClick: () => { onClose?.(); onUpdateLog?.(); }
        // },
    ];

    const formatTime = (timeStr) => {
        const date = new Date(timeStr);
        return date.toLocaleString('zh-CN', {
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 z-[100]"
                    />

                    {/* Popover Container - 鏇磋嚧瀵嗙殑瀹藉害鍜屾洿鐜颁唬鐨勯槾褰?*/}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 10, x: 0 }}
                        animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10, x: 0 }}
                        className="fixed bottom-6 left-20 z-[101] w-[340px] bg-white/95 backdrop-blur-xl rounded-[32px] shadow-[0_20px_60px_rgba(94,74,227,0.15)] border-2 border-white overflow-hidden ring-4 ring-[#EFF6FF]"
                    >
                        {/* User Profile Header */}
                        <div className="px-6 py-6 pb-4 bg-[#EFF6FF]">
                            <div className="flex items-center gap-4">
                                <div className="flex-shrink-0">
                                    <div className="w-14 h-14 rounded-full ring-4 ring-white bg-white overflow-hidden flex items-center justify-center shadow-sm">
                                        <Avatar
                                            size={56}
                                            name={String(userData?.username || userData?.id || '')}
                                            variant="beam"
                                            colors={['#2563EB', '#3B82F6', '#6C5CE7', '#FF6B6B', '#A8E6CF']}
                                        />
                                    </div>
                                </div>

                                {/* User Info */}
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-lg font-black text-[#4A4A4A] truncate tracking-tight flex items-center gap-2">
                                        {userData.username}
                                        <span className="bg-[#3B82F6] text-[10px] px-1.5 py-0.5 rounded-full text-[#4A4A4A] font-bold">VIP</span>
                                    </h3>
                                    <p className="text-xs text-[#888] truncate mt-0.5 font-bold">
                                        {userData.email || userData.phone || '未绑定邮箱'}
                                    </p>
                                </div>

                                {loading && (
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                        className="text-[#2563EB]"
                                    >
                                        <RefreshCw className="w-5 h-5" />
                                    </motion.div>
                                )}
                            </div>
                        </div>

                        <div className="relative overflow-hidden bg-white">
                            {/* Main Menu Section */}
                            {activeSection === 'main' && (
                                <motion.div
                                    initial={{ opacity: 0, x: 0 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -100 }}
                                >
                                    {/* Points Card */}
                                    <div className="px-5 pb-5 pt-4">
                                        <div className="bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] rounded-3xl p-5 flex items-center justify-between shadow-lg shadow-[#2563EB]/30 text-white relative overflow-hidden group">
                                            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full blur-xl transform translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-700"></div>

                                            <div className="flex flex-col relative z-10">
                                                <span className="text-[11px] font-bold text-white/80 uppercase tracking-widest mb-1 flex items-center gap-1">
                                                    <Diamond className="w-3 h-3" fill="currentColor" /> 算力余额
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-3xl font-black leading-none tracking-tight">
                                                        {userData.points_balance?.toLocaleString() || 0}
                                                    </span>
                                                    <span className="text-sm font-bold opacity-80">PTS</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={onRecharge}
                                                className="h-10 px-5 bg-white text-[#2563EB] rounded-full text-xs font-black transition-all hover:scale-105 active:scale-95 shadow-md"
                                            >
                                                充值
                                            </button>
                                        </div>
                                    </div>

                                    {/* Menu List - 浼樺寲闂磋窛鍜屼氦浜?*/}
                                    <div className="px-3 pb-3 space-y-2">
                                        {menuItems.map((item, index) => (
                                            <button
                                                key={index}
                                                onClick={item.onClick}
                                                className="w-full h-[56px] flex items-center gap-3 px-4 rounded-[20px] hover:bg-[#EFF6FF] transition-all group border border-transparent hover:border-[#BFDBFE]"
                                            >
                                                <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-[#888] bg-[#F9F9F9] group-hover:text-[#2563EB] group-hover:bg-white group-hover:shadow-sm transition-all">
                                                    <item.icon className="w-5 h-5" />
                                                </div>
                                                <span className="flex-1 text-left text-sm font-bold text-[#4A4A4A] group-hover:text-[#2563EB]">
                                                    {item.label}
                                                </span>
                                                {item.badge && (
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${item.badge === 'HOT'
                                                        ? 'bg-[#ffe8e8] text-[#ff6b6b]'
                                                        : 'bg-[#DBEAFE] text-[#3B82F6]'
                                                        }`}>
                                                        {item.badge}
                                                    </span>
                                                )}
                                                {item.isExternal ? (
                                                    <ExternalLink className="w-4 h-4 text-[#DDD] group-hover:text-[#2563EB] transition-all" />
                                                ) : (
                                                    <ChevronRight className="w-4 h-4 text-[#DDD] group-hover:text-[#2563EB] group-hover:translate-x-1 transition-all" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            {/* History Section - 璧勪骇娑堣垂璁板綍 */}
                            {activeSection === 'history' && (
                                <motion.div
                                    initial={{ opacity: 0, x: 100 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="px-5 pb-4"
                                >
                                    <div className="flex items-center gap-2 mb-4">
                                        <button
                                            onClick={() => setActiveSection('main')}
                                            className="p-1.5 hover:bg-[#EFF6FF] rounded-full transition-colors text-[#888] hover:text-[#4A4A4A]"
                                        >
                                            <ChevronRight className="w-5 h-5 rotate-180" />
                                        </button>
                                        <h4 className="font-bold text-[#4A4A4A] text-base">资产消费记录</h4>
                                    </div>

                                    <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1 scrollbar-thin">
                                        {data?.transactions?.results?.length > 0 ? (
                                            data.transactions.results.map((t, idx) => {
                                                // 瑙ｆ瀽琛屼负绫诲瀷
                                                const getActionLabel = (desc) => {
                                                    if (desc?.includes('提取角色')) return '提取角色';
                                                    if (desc?.includes('生成视频')) return '生成视频';
                                                    if (desc?.includes('充值')) return '充值';
                                                    if (desc?.includes('退款')) return '退款';
                                                    return desc?.split(' ')[0] || '消费';
                                                };
                                                const actionLabel = getActionLabel(t.description);
                                                const isIncome = t.transaction_type === 'recharge' || t.transaction_type === 'refund';

                                                return (
                                                    <div key={idx} className="flex items-center gap-3 p-3 rounded-2xl bg-[#FAFAFA] border border-[#F0F0F0] hover:border-[#BFDBFE] transition-colors">
                                                        {/* 鍥炬爣 */}
                                                        <div className={`w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center ${isIncome
                                                            ? 'bg-[#E0F7FA] text-[#00BCD4]'
                                                            : 'bg-[#EFF6FF] text-[#2563EB]'
                                                            }`}>
                                                            {isIncome ? <CreditCard className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                                                        </div>

                                                        {/* 鍐呭鍖?*/}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-bold text-[#4A4A4A]">{actionLabel}</span>
                                                                <span className={`text-base font-black ${isIncome ? 'text-[#00BCD4]' : 'text-[#2563EB]'}`}>
                                                                    {isIncome ? '+' : '-'}{Math.abs(Number(t.amount || 0))}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                <span className="text-xs text-[#AAA] font-bold">{formatTime(t.created_at)}</span>
                                                                {t.asset_id && (
                                                                    <span className="text-[10px] text-[#DDD] font-mono truncate max-w-[120px]" title={t.asset_id}>
                                                                        #{t.asset_id.slice(-12)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="py-16 flex flex-col items-center justify-center text-[#CCC]">
                                                <History className="w-12 h-12 mb-3 opacity-20" />
                                                <span className="text-sm font-bold">暂无消费记录</span>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}

                            {/* Invite Section - 閭€璇峰ソ鍙?*/}
                            {activeSection === 'invite' && (
                                <motion.div
                                    initial={{ opacity: 0, x: 100 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="px-5 pb-4"
                                >
                                    <div className="flex items-center gap-2 mb-4">
                                        <button
                                            onClick={() => setActiveSection('main')}
                                            className="p-1.5 hover:bg-[#EFF6FF] rounded-full transition-colors text-[#888] hover:text-[#4A4A4A]"
                                        >
                                            <ChevronRight className="w-5 h-5 rotate-180" />
                                        </button>
                                        <h4 className="font-bold text-[#4A4A4A] text-base">邀请好友赚算力</h4>
                                    </div>

                                    <div className="py-16 flex flex-col items-center justify-center text-[#CCC]">
                                        <UserPlus className="w-12 h-12 mb-3 opacity-20 text-[#3B82F6]" />
                                        <span className="text-sm font-bold">邀请功能即将上线</span>
                                        <span className="text-xs mt-1 text-[#DDD]">敬请期待</span>
                                    </div>
                                </motion.div>
                            )}


                        </div>

                        {/* Footer / Logout */}
                        <div className="px-3 pb-4 pt-1 mb-2">
                            <button
                                onClick={onLogout}
                                className="w-full h-[56px] flex items-center gap-3 px-4 rounded-[20px] hover:bg-[#FFF0F0] transition-all group border border-transparent hover:border-[#FFD9D9]"
                            >
                                <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-[#FF6B6B] bg-[#FFF5F5] group-hover:bg-[#FFEDED] transition-all">
                                    <LogOut className="w-5 h-5" />
                                </div>
                                <span className="text-sm font-bold text-[#FF6B6B]">
                                    退出登录
                                </span>
                            </button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default UserCenter;

